'use client'
// components/Pages/LinkTabsBlock.jsx
// Content block type 'link_tabs': a row of tabs with a sliding underline and,
// under it, a grid of links for the active tab (e.g. "Browse Categories").
//
// block = {
//   type: 'link_tabs',
//   tabs: [{ id, label: {lang}, links: [{ id, label: {lang}, url }] }],
//   // all optional ↓
//   boxBg, boxBorderColor, boxShadow, boxPadding, boxRadius, boxTopLineColor,
//   tabColor, tabActiveColor, tabSize, tabSizeMobile, tabWeight, tabActiveWeight,
//   tabsBorderColor, underlineColor, separator ('|'), separatorColor, separatorGap,
//   linkColor, linkHoverColor, linkHoverBg, linkBorderColor, linkSize, linkOpacity,
//   chevronColor, colsMobile (2), colsSm (3), colsMd (3), colsLg (4), linkGap
// }
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

const LANGS = ['en', 'cn', 'th', 'ru', 'fr', 'de', 'it', 'es', 'ja', 'ko', 'tr', 'he', 'ar']
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

const ml = (v, lang) => (v && typeof v === 'object') ? (v[lang] ?? v.en ?? '') : (v ?? '')

const hrefFor = (url, lang) => {
  if (!url || url === '#') return '#'
  if (/^(https?:|tel:|mailto:)/.test(url)) return url
  const path = url.startsWith('/') ? url : `/${url}`
  return LANGS.includes(lang) ? `/${lang}${path}` : path
}

const Chevron = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 18 6-6-6-6" />
  </svg>
)

export default function LinkTabsBlock({ block, lang, uid = 'lt' }) {
  const tabs = (block.tabs ?? []).filter(t => ml(t.label, lang))
  const [active, setActive] = useState(0)
  const [bar, setBar] = useState({ left: 0, width: 0 })
  const tabRefs = useRef([])
  const isRTL = lang === 'he' || lang === 'ar'

  const measure = () => {
    const el = tabRefs.current[active]
    if (el) setBar({ left: el.offsetLeft, width: el.offsetWidth })
  }
  useIsoLayoutEffect(measure, [active, lang, tabs.length])
  useEffect(() => {
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  })

  if (!tabs.length) return null

  const c = (k, d) => block[k] ?? d
  const cls = `${uid}-lt`
  const links = tabs[Math.min(active, tabs.length - 1)]?.links ?? []

  const css = `
    .${cls}-tab { padding: 4px 0; background: none; border: 0; cursor: pointer; flex-shrink: 0; white-space: nowrap;
      font-size: ${c('tabSizeMobile', '1rem')}; font-weight: ${c('tabWeight', 400)}; color: ${c('tabColor', '#ffffff')};
      transition: color .3s ease; line-height: 1.5; }
    .${cls}-tab:hover { font-weight: ${c('tabActiveWeight', 700)}; }
    .${cls}-tab[aria-selected=true] { color: ${c('tabActiveColor', 'var(--thirdcolor)')}; font-weight: ${c('tabActiveWeight', 700)}; }
    .${cls}-sep { padding: 0 ${c('separatorGap', '0.75rem')}; color: ${c('separatorColor', '#d6d3d1')}; font-size: 0.875rem; user-select: none; flex-shrink: 0; }
    .${cls}-grid { display: grid; gap: ${c('linkGap', '4px')}; grid-template-columns: repeat(${c('colsMobile', 2)}, minmax(0, 1fr)); }
    .${cls}-link { display: flex; align-items: center; gap: 8px; padding: 8px 10px; text-decoration: none;
      font-size: ${c('linkSize', '0.875rem')}; color: ${c('linkColor', '#ffffff')}; opacity: ${c('linkOpacity', 0.85)};
      border-bottom: 1px solid ${c('linkBorderColor', 'rgba(255,255,255,0.05)')};
      transition: all .2s ease; }
    .${cls}-link:hover { opacity: 1; color: ${c('linkHoverColor', 'var(--thirdcolor)')}; background: ${c('linkHoverBg', 'rgba(255,255,255,0.05)')};
      transform: translateX(${isRTL ? '-4px' : '4px'}); }
    .${cls}-link:hover .${cls}-chev { transform: scale(1.1); }
    .${cls}-chev { color: ${c('chevronColor', 'var(--thirdcolor)')}; flex-shrink: 0; display: inline-flex; transition: transform .2s ease; ${isRTL ? 'transform: scaleX(-1);' : ''} }
    .${cls}-track { overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; }
    .${cls}-track::-webkit-scrollbar { display: none; }
    @media (min-width: 640px) {
      .${cls}-tab { font-size: ${c('tabSize', '1.125rem')}; }
      .${cls}-grid { grid-template-columns: repeat(${c('colsSm', 3)}, minmax(0, 1fr)); }
    }
    @media (min-width: 768px) {
      .${cls}-sep { padding: 0 ${c('separatorGapMd', '1.25rem')}; }
      .${cls}-grid { grid-template-columns: repeat(${c('colsMd', 3)}, minmax(0, 1fr)); }
    }
    @media (min-width: 1024px) {
      .${cls}-grid { grid-template-columns: repeat(${c('colsLg', 4)}, minmax(0, 1fr)); }
    }
  `

  return (
    <div dir={isRTL ? 'rtl' : undefined} style={{
      position: 'relative', width: '100%', overflow: 'hidden', boxSizing: 'border-box',
      background:   c('boxBg', 'linear-gradient(to bottom right, rgba(10,19,13,0.9), rgba(16,29,20,0.8), rgba(10,19,13,0.9))'),
      border:       `1px solid ${c('boxBorderColor', 'rgba(201,169,110,0.25)')}`,
      boxShadow:    c('boxShadow', '0 12px 40px rgba(0,0,0,0.45)'),
      borderRadius: c('boxRadius', 0),
      padding:      c('boxPadding', '1.5rem'),
    }}>
      <style>{css}</style>
      {c('boxTopLineColor', 'rgba(201,169,110,0.5)') !== 'none' && (
        <div aria-hidden="true" style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 1,
          background: `linear-gradient(to right, transparent, ${c('boxTopLineColor', 'rgba(201,169,110,0.5)')}, transparent)`,
        }} />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ position: 'relative', borderBottom: `1px solid ${c('tabsBorderColor', 'rgba(231,229,228,0.2)')}`, paddingBottom: 8 }}>
          <div className={`${cls}-track`} style={{ position: 'relative' }}>
            <div role="tablist" style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', minWidth: '100%' }}>
              {tabs.map((tab, i) => (
                <span key={tab.id ?? i} style={{ display: 'contents' }}>
                  {i > 0 && <span className={`${cls}-sep`} aria-hidden="true">{c('separator', '|')}</span>}
                  <button type="button" role="tab" aria-selected={i === active}
                    ref={el => { tabRefs.current[i] = el }}
                    className={`${cls}-tab`} onClick={() => setActive(i)}>
                    {ml(tab.label, lang)}
                  </button>
                </span>
              ))}
            </div>
            <div aria-hidden="true" style={{
              position: 'absolute', bottom: 0, height: 1, left: bar.left, width: bar.width,
              background: c('underlineColor', 'var(--thirdcolor)'), transition: 'all .3s ease-out',
            }} />
          </div>
        </div>
        <div className={`${cls}-grid`} role="tabpanel">
          {links.map((link, i) => {
            const label = ml(link.label, lang)
            if (!label) return null
            const href = hrefFor(link.url, lang)
            const external = /^https?:/.test(href)
            return (
              <a key={link.id ?? i} href={href} title={label} className={`${cls}-link`}
                target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}
                style={i === links.length - 1 ? { borderBottom: 0 } : undefined}>
                <span className={`${cls}-chev`}><Chevron size={14} /></span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
              </a>
            )
          })}
        </div>
      </div>
    </div>
  )
}